import { useEffect, useState } from 'react';
import { Field, Select } from './ui';
import { api } from '../api';

/*
 * Billing (legal) entity — colour coding + filter, shared across views.
 *
 * A project's billing entity is the Magnetic legal entity it invoices
 * under (Platform45 (Pty) Ltd / Platform45 Limited / Next45 (Pty) Ltd).
 * Colours are stable per entity so a given entity always reads the same
 * across Project Margins, the KPI dashboard and Reconciliation.
 */

// Fixed palette — solid classes (app commits to the dark theme, like Pill).
const PALETTE = [
  { dot: 'bg-sky-400',     badge: 'bg-sky-500/15 text-sky-300',        ring: 'ring-sky-500/40' },
  { dot: 'bg-emerald-400', badge: 'bg-emerald-500/15 text-emerald-300', ring: 'ring-emerald-500/40' },
  { dot: 'bg-amber-400',   badge: 'bg-amber-500/15 text-amber-300',     ring: 'ring-amber-500/40' },
  { dot: 'bg-violet-400',  badge: 'bg-violet-500/15 text-violet-300',   ring: 'ring-violet-500/40' },
  { dot: 'bg-rose-400',    badge: 'bg-rose-500/15 text-rose-300',       ring: 'ring-rose-500/40' },
];

// Pin the known P45 entities to specific hues; anything else hashes in.
const FIXED = {
  'platform45 (pty) ltd': 0,   // sky
  'platform45 limited':   1,   // emerald
  'next45 (pty) ltd':     2,   // amber
};

function paletteIndex(name) {
  const key = (name || '').trim().toLowerCase();
  if (key in FIXED) return FIXED[key];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % PALETTE.length;
}

export function entityStyle(name) {
  return PALETTE[paletteIndex(name)];
}

// Short label — drops the legal suffix so tables stay tight.
export function entityShort(name) {
  if (!name) return '—';
  return name.replace(/\s*\((pty|proprietary)\)?\s*(ltd|limited)?\.?$/i, '')
             .replace(/\s+limited$/i, '')
             .trim() || name;
}

export function BillingEntityBadge({ name, title }) {
  if (!name || name === '—') {
    return <span className="text-slate-500 text-[11px]">—</span>;
  }
  const s = entityStyle(name);
  return (
    <span
      title={title || name}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium tracking-tight ${s.badge}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {entityShort(name)}
    </span>
  );
}

/*
 * Self-loading entity filter. `value` is the entity id (or '' for all);
 * `onChange` receives the id string. Renders nothing until entities load,
 * and hides itself if only one entity exists (nothing to filter).
 */
export function BillingEntityFilter({ value, onChange, label = 'Billing entity' }) {
  const [entities, setEntities] = useState([]);
  useEffect(() => {
    api.projects.billingEntities().then(setEntities).catch(() => setEntities([]));
  }, []);
  if (entities.length <= 1) return null;
  return (
    <Field label={label}>
      <Select value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">All entities</option>
        {entities.map((e) => (
          <option key={e.id} value={e.id}>
            {entityShort(e.name)} ({e.project_count})
          </option>
        ))}
      </Select>
    </Field>
  );
}
